// One conversation: glass header, the timeline on the wallpaper, the floating composer.
//
// The timeline is a `column-reverse` scroller: its natural resting place IS the newest message,
// so a conversation opens at the bottom on its FIRST frame with no scroll-to-bottom jump, older
// history added at the top never moves what the reader is looking at, and `content-visibility`
// lets the browser skip laying out rows that are off screen. That is the whole virtualisation —
// the Mac needed an AppKit table and six releases of anchoring fixes (ADR-002) for what the
// browser's own layout gives here for free.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { data } from '../../bridge/client';
import type { ChannelPage } from '../../bridge/protocol';
import { userDisplayName } from '../../core/channelListDerivation';
import { ConversationLanding } from '../../core/conversationLanding';
import { QuickReactions } from '../../core/quickReactions';
import { PostState, type PostRecord } from '../../core/records';
import { TypingIndicatorLabel } from '../../core/typingIndicatorLabel';
import { Icon } from '../../design/icons';
import { t, tf } from '../../i18n/i18n';
import { useLive, useMinuteClock } from '../hooks';
import { buildConversation } from '../models/conversationModel';
import { endChannelSwitch } from '../perf';
import { cachedPage, FIRST_PAGE, remember, takeInflight } from '../pageCache';
import { Composer } from './Composer';
import { MessageRow, type RowActions } from './MessageRow';
import { useRowActions } from './rowActions';
import type { BodyContext } from './MessageBody';

const PAGE_STEP = 30;

/** Open ON a message (search hit, saved, permalink): the window around it, flashed, with the way back. */
export interface JumpAnchor {
  channelId: string;
  postId: string;
  createAt: number;
  token: number;
}

export interface ConversationProps {
  channelId: string;
  previousChannelId: string | null;
  title: string;
  subtitle: string;
  icon: 'hash' | 'lock' | 'dm' | 'group';
  unreadBoundary: number | null;
  me: string;
  myUsername: string | null;
  typists: string[];
  readOnlyNotice: string | null;
  onOpenThread(rootId: string): void;
  onOpenUser(username: string, anchor?: { x: number; y: number }): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onOpenInfo(tab?: 'info' | 'members' | 'pinned' | 'files'): void;
  onSearchHere(): void;
  onToast(message: string, tone?: 'info' | 'error'): void;
  teamName: string;
  jump: JumpAnchor | null;
  onLeaveJump(): void;
  connectionStrip: string | null;
  headerLeading?: ReactNode;
}

const PAGE_TABLES = ['posts', 'postFiles', 'postReactions', 'users', 'postSeenSummaries', 'userStatuses', 'channels', 'customEmoji'] as const;

export function useQuickReactions(): string[] {
  return useMemo(() => {
    let raw = '';
    try {
      raw = localStorage.getItem(QuickReactions.recentsKey) ?? '';
    } catch {
      /* defaults */
    }
    return QuickReactions.names(raw, 6);
  }, []);
}

export function rememberReaction(name: string): void {
  try {
    const key = QuickReactions.recentsKey;
    const list = (localStorage.getItem(key) ?? '').split(',').filter(Boolean).filter((n) => n !== name);
    localStorage.setItem(key, [name, ...list].slice(0, 12).join(','));
  } catch {
    /* a per-viewer convenience */
  }
}

export function Conversation(p: ConversationProps) {
  const [limit, setLimit] = useState(FIRST_PAGE);
  const [echoes, setEchoes] = useState<PostRecord[]>([]);
  const [olderState, setOlderState] = useState<{ loading: boolean; failed: boolean; hasMore: boolean }>({ loading: false, failed: false, hasMore: true });
  const [atBottom, setAtBottom] = useState(true);
  const scroller = useRef<HTMLDivElement>(null);
  const topSentinel = useRef<HTMLDivElement>(null);
  const now = useMinuteClock();
  const quickReactions = useQuickReactions();
  const switchReported = useRef(false);

  const anchored = p.jump;
  const { value: page, loadedFor } = useLive<ChannelPage>(
    () => (anchored ? data.call('channelPageAround', p.channelId, anchored.createAt) : data.call('channelPage', p.channelId, limit)),
    [...PAGE_TABLES],
    [p.channelId, limit, anchored?.token ?? 0],
    {
      // The click already asked (pageCache.prefetchPage on mousedown); a revisited conversation
      // paints its last page on the first frame while the store is re-read underneath.
      initial: () => (anchored ? undefined : cachedPage(p.channelId) ?? undefined),
      first: () => (!anchored && limit === FIRST_PAGE ? takeInflight(p.channelId) : null),
    },
  );
  // The jumped-to message: centred and marked, resting at half strength for twelve seconds
  // (JumpBlink.holdMillis) — three blinks and gone was the complaint fixed on every client.
  const [markedId, setMarkedId] = useState<string | null>(null);
  useEffect(() => {
    if (!anchored) return;
    setMarkedId(anchored.postId);
    const id = setTimeout(() => setMarkedId(null), 12_000);
    return () => clearTimeout(id);
  }, [anchored?.token]);
  // Reset on a new conversation; open it (optimistic read clear + newest page in the background).
  useEffect(() => {
    setLimit(FIRST_PAGE);
    setEchoes([]);
    setOlderState({ loading: false, failed: false, hasMore: true });
    switchReported.current = false;
    let alive = true;
    void data.call('openChannel', p.channelId, p.previousChannelId).then((r) => {
      if (alive) setOlderState((s) => ({ ...s, hasMore: r.hasOlder }));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.channelId]);

  const current = page && page.channelId === p.channelId ? page : null;
  useEffect(() => {
    if (current && !anchored && current.posts.length <= FIRST_PAGE) remember(current);
  }, [current, anchored]);
  useEffect(() => {
    if (!anchored || !current) return;
    requestAnimationFrame(() => {
      document.querySelector(`.conversation-pane:not(.side-panel) [data-post-id="${anchored.postId}"]`)?.scrollIntoView({ block: 'center' });
    });
  }, [anchored?.token, current?.posts.length]);

  const model = useMemo(
    () => (current ? buildConversation(current, p.me, p.unreadBoundary, now, echoes.filter((e) => e.channelId === p.channelId)) : null),
    [current, p.me, p.unreadBoundary, now, echoes, p.channelId],
  );

  // channel switch → messages painted (docs/PERFORMANCE.md budget: < 100 ms)
  useLayoutEffect(() => {
    if (model && !switchReported.current && (loadedFor === 'cache' || loadedFor.includes(p.channelId))) {
      switchReported.current = true;
      endChannelSwitch(p.channelId, model.rows.length);
    }
  }, [model, loadedFor, p.channelId]);

  // A row that cannot name its author goes and FETCHES the profile.
  useEffect(() => {
    if (model?.unknownAuthorIds.length) void data.call('resolveUsers', model.unknownAuthorIds);
  }, [model?.unknownAuthorIds.join(',')]);

  // Echoes yield to the stored row the moment it exists.
  useEffect(() => {
    if (!current || !echoes.length) return;
    const stored = new Set(current.posts.flatMap((x) => [x.id, x.pendingId ?? '']));
    const left = echoes.filter((e) => !stored.has(e.id));
    if (left.length !== echoes.length) setEchoes(left);
  }, [current, echoes]);

  const loadOlder = useCallback(async () => {
    if (!current || olderState.loading) return;
    const oldest = current.posts[current.posts.length - 1];
    if (!oldest) return;
    setOlderState((s) => ({ ...s, loading: true, failed: false }));
    const r = await data.call('loadOlder', p.channelId, oldest.id);
    if (r.kind === 'failed') setOlderState((s) => ({ ...s, loading: false, failed: true }));
    else {
      setOlderState({ loading: false, failed: false, hasMore: r.kind === 'page' ? !!r.hasMore : false });
      setLimit((l) => l + PAGE_STEP);
    }
  }, [current, olderState.loading, p.channelId]);

  // The top of history asks for more only when the reader actually reaches it, and a FAILURE
  // stops the automatic asking (a geometry trigger would otherwise retry on every layout pass).
  useEffect(() => {
    const el = topSentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (anchored) return;
        const cachedMore = current ? current.posts.length >= limit : false;
        if (!visible) return;
        if (cachedMore) setLimit((l) => l + PAGE_STEP);
        else if (ConversationLanding.mayLoadOlder({ hasLanded: true, isLoadingOlder: olderState.loading, hasMoreOlder: olderState.hasMore, lastAttemptFailed: olderState.failed }))
          void loadOlder();
      },
      { root: scroller.current, rootMargin: '600px 0px 0px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [current, limit, olderState, loadOlder, anchored]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    // column-reverse: 0 is the bottom, negative values go up.
    setAtBottom(el.scrollTop > -80);
  };

  const scrollToBottom = () => scroller.current?.scrollTo({ top: 0, behavior: 'smooth' });

  const send = (message: string) => {
    const pendingId = crypto.randomUUID().replace(/-/g, '');
    const createAt = Date.now();
    // On screen THIS frame — "I sent it and it appeared after a while" is the send path's one
    // unforgivable failure (2026-09-04). The store row replaces the echo when it lands.
    setEchoes((e) => [
      ...e,
      {
        id: pendingId,
        channelId: p.channelId,
        userId: p.me,
        createAt,
        updateAt: createAt,
        deleteAt: 0,
        message,
        rootId: null,
        pendingId,
        state: PostState.Pending,
        type: '',
        props: null,
        isPinned: false,
        editAt: 0,
        embedJson: null,
        priority: '',
      },
    ]);
    if (anchored) p.onLeaveJump();
    scroller.current?.scrollTo({ top: 0 });
    void data.call('send', p.channelId, message, null, pendingId, createAt).then((r) => {
      if (r.refused) console.warn('[satori] refused:', r.refused);
    });
  };

  const actions: RowActions = useRowActions({
    onOpenThread: p.onOpenThread,
    onOpenUserId: p.onOpenUserId,
    onToast: p.onToast,
    teamName: p.teamName,
    quickReactions,
  });

  const ctx: BodyContext = useMemo(
    () => ({
      knownUsernames: model?.knownUsernames ?? new Set(),
      currentUsername: p.myUsername,
      customEmoji: current?.customEmoji ?? {},
      onMention: (username: string, anchor?: { x: number; y: number }) => p.onOpenUser(username, anchor),
    }),
    [model?.knownUsernames, p.myUsername, current?.customEmoji, p.onOpenUser],
  );

  const savedSet = useMemo(() => new Set(current?.saved ?? []), [current?.saved]);
  const participants = useMemo(() => (model ? [...model.usersById.values()].filter((u) => u.id !== p.me) : []), [model, p.me]);
  const topState = ConversationLanding.topState({ isLoadingOlder: olderState.loading, olderLoadFailed: olderState.failed, hasMoreOlder: olderState.hasMore || (current ? current.posts.length >= limit : true) });
  const typingText = TypingIndicatorLabel.text(
    p.typists.map((id) => {
      const u = model?.usersById.get(id);
      return u ? userDisplayName(u) : t('mac.unknownAuthor');
    }),
    [],
  );

  return (
    <section className="conversation-pane" aria-label={p.title}>
      <div className="wallpaper" />
      <header className="conversation-header">
        {p.headerLeading}
        <span style={{ color: 'var(--text-muted)', display: 'grid' }}>
          <Icon name={p.icon === 'lock' ? 'lock' : p.icon === 'hash' ? 'hash' : p.icon === 'group' ? 'threads' : 'at'} size={18} />
        </span>
        <div className="title-block">
          <h1>{p.title}</h1>
          {p.subtitle ? <div className="subtitle">{p.subtitle}</div> : null}
        </div>
        <div className="actions">
          <button className="icon-button" onClick={p.onSearchHere} title={t('web.nav.searchHere')} aria-label={t('web.nav.searchHere')}>
            <Icon name="search" size={17} />
          </button>
          <button className="icon-button" onClick={() => p.onOpenInfo('pinned')} title={t('channel.pinned.title')} aria-label={t('channel.pinned.title')}>
            <Icon name="pin" size={17} />
          </button>
          <button className="icon-button" onClick={() => p.onOpenInfo('info')} title={t('web.nav.info')} aria-label={t('web.nav.info')}>
            <Icon name="info" size={17} />
          </button>
        </div>
      </header>
      {p.connectionStrip ? (
        <div className="connection-strip" role="status">
          <Icon name="retry" size={13} />
          {p.connectionStrip}
        </div>
      ) : null}
      <div className="timeline" ref={scroller} onScroll={onScroll}>
        <div className="timeline-inner">
          <div ref={topSentinel} />
          {model ? (
            <>
              {topState === 'loading' ? <div className="top-of-history">{t('channel.loading.accessibility')}…</div> : null}
              {topState === 'failed' ? (
                <div className="top-of-history">
                  <button onClick={() => void loadOlder()}>{t('channel.history.retry')}</button>
                </div>
              ) : null}
              {topState === 'beginning' && model.rows.length ? <div className="top-of-history">{t('channel.history.beginning')}</div> : null}
              {model.rows.length === 0 && current?.historyReach === 'start' ? (
                <div className="empty-state">
                  <h2>{t('channel.empty.title')}</h2>
                  <p>{t('channel.empty.description')}</p>
                </div>
              ) : null}
              {model.rows.map((row) => {
                const author = model.usersById.get(row.post.userId);
                return (
                  <MessageRow
                    key={row.post.id}
                    row={row}
                    authorName={model.authorName(row.post)}
                    authorVersion={author?.lastPictureUpdate ?? 0}
                    presence={current?.statuses[row.post.userId] ?? null}
                    isOwn={row.post.userId === p.me}
                    files={current?.files[row.post.id]}
                    chips={model.chips.get(row.post.id)}
                    footer={model.footers.get(row.post.id)}
                    footerNames={(id) => {
                      const u = model.usersById.get(id);
                      return { name: u ? userDisplayName(u) : '?', version: u?.lastPictureUpdate ?? 0 };
                    }}
                    seen={model.seen.get(row.post.id) ?? null}
                    saved={savedSet.has(row.post.id)}
                    marked={markedId === row.post.id}
                    ctx={ctx}
                    actions={actions}
                    now={now}
                  />
                );
              })}
            </>
          ) : null}
        </div>
      </div>
      {typingText ? (
        <div className="typing" role="status">
          <span className="typing-dots">
            <i />
            <i />
            <i />
          </span>
          {typingText}
        </div>
      ) : null}
      {anchored ? (
        <button
          className="jump-pill"
          onClick={() => {
            p.onLeaveJump();
            requestAnimationFrame(() => scroller.current?.scrollTo({ top: 0 }));
          }}
        >
          <Icon name="arrowDown" size={14} />
          {t('web.nav.backToRecents')}
        </button>
      ) : !atBottom ? (
        <button className="jump-pill" onClick={scrollToBottom}>
          <Icon name="arrowDown" size={14} />
          {t('channel.jump.toNewest')}
        </button>
      ) : null}
      {current ? (
      <Composer
        key={p.channelId}
        banner={
          model && model.failedCount > 0 ? (
            <div className="undelivered" role="alert">
              <Icon name="alertCircle" size={15} />
              {model.failedCount === 1 ? t('channel.undeliveredOne') : tf('channel.undeliveredMany', model.failedCount)}
              <span className="spacer" />
            </div>
          ) : null
        }
        channelKey={p.channelId}
        initialDraft={current?.draft ?? ''}
        placeholder={t('mac.composerPlaceholder')}
        participants={participants}
        customEmojiNames={Object.keys(current?.customEmoji ?? {})}
        onSend={send}
        onDraft={(m) => void data.call('saveDraft', p.channelId, '', m)}
        onTyping={() => void data.call('userTyping', p.channelId, '')}
        disabledNotice={p.readOnlyNotice}
      />
      ) : null}
    </section>
  );
}
